/**
 * Мапа розташування СЕС для дашборду (одноактивний вигляд, напр. акаунт Табаки).
 * react-leaflet + CARTO-тайли (світла/темна), маркер станції зеленою крапкою
 * (догма: генерація). Скляна плитка .tile як контейнер.
 */
import { useRef, type CSSProperties } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip } from 'react-leaflet';
import { Plus, Minus } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '@/hooks/useTheme';

export default function StationMap({ lat, lon, name, height = 260, className = '' }:
  { lat: number; lon: number; name: string; height?: number | string; className?: string }) {
  const { isDark } = useTheme();
  const mapRef = useRef<any>(null);
  // OSM-тайли (без API-ключа). У темній темі інвертуємо ТАЙЛИ CSS-фільтром. Клас
  // hmap-dark — на ЗОВНІШНЬОМУ div (реактивний), тож фільтр перемикається разом
  // із темою; маркер у overlay-панелі не інвертується й лишається зеленим.
  const ok = isDark ? '#4CAF6A' : '#2E7D46';
  // Пілюля зуму: напівпрозора підкладка за темою (контраст над будь-якою мапою).
  const pillBg = isDark ? 'rgba(24,30,37,0.72)' : 'rgba(255,255,255,0.82)';
  const pillBorder = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(40,50,60,0.14)';
  const zoomBtn: CSSProperties = {
    width: 34, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--text)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
  };
  return (
    <div className={`tile overflow-hidden relative ${className}`} style={{ padding: 0 }}>
      <MapContainer ref={mapRef} center={[lat, lon]} zoom={9} scrollWheelZoom={false}
        attributionControl={false} zoomControl={false}
        style={{ height, width: '100%', background: 'var(--surface-2)' }}>
        {/* Esri Gray Canvas (безключова): база + підписи (Reference), світла/темна. */}
        <TileLayer key={isDark ? 'db' : 'lb'} attribution="Tiles &copy; Esri"
          url={isDark
            ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
            : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'} />
        <TileLayer key={isDark ? 'dr' : 'lr'}
          url={isDark
            ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
            : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}'} />
        <CircleMarker center={[lat, lon]} radius={9}
          pathOptions={{ color: ok, fillColor: ok, fillOpacity: 0.5, weight: 2 }}>
          <LTooltip>{name}</LTooltip>
        </CircleMarker>
      </MapContainer>
      {/* Zoom: напівпрозора пілюля з blur (контраст в обох темах), закруглена, Lucide +/−. */}
      <div className="absolute top-2 left-2 z-[1000] flex flex-col overflow-hidden"
        style={{ background: pillBg, border: `1px solid ${pillBorder}`, borderRadius: 12,
                 backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
        <button type="button" aria-label="Збільшити" onClick={() => mapRef.current?.zoomIn()} style={zoomBtn}>
          <Plus size={18} strokeWidth={2.5} />
        </button>
        <div style={{ height: 1, background: pillBorder }} />
        <button type="button" aria-label="Зменшити" onClick={() => mapRef.current?.zoomOut()} style={zoomBtn}>
          <Minus size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
